import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Switch,
  FormControlLabel,
  Tooltip,
  Chip,
  Stack,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import api from '../api/axios';

interface TenantOption {
  id: number;
  name: string;
}

interface ConfigRow {
  platform_id: number;
  platform_name: string;
  platform_display_name: string;
  redeem_to_platform_enabled: boolean;
  supports_balance_read: boolean;
  supports_redeem: boolean;
  min_redeem: number | null;
  max_redeem: number | null;
  configured: boolean;
  saving: boolean;
  error: string | null;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info';
}

const PlatformRedeemConfigPage: React.FC = () => {
  const isSuperAdmin = localStorage.getItem('userRole') === 'super_admin';
  const userTenantIdStr = localStorage.getItem('userTenantId');

  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<number | ''>('');
  const [rows, setRows] = useState<ConfigRow[]>([]);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  const updateRow = (platformId: number, changes: Partial<ConfigRow>) => {
    setRows(prev => prev.map(r => r.platform_id === platformId ? { ...r, ...changes } : r));
  };

  const validate = (row: ConfigRow): string | null => {
    if (row.min_redeem !== null && row.min_redeem < 0) return 'Min Redeem cannot be negative.';
    if (row.max_redeem !== null && row.max_redeem < 0) return 'Max Redeem cannot be negative.';
    if (row.min_redeem !== null && row.max_redeem !== null && row.min_redeem > row.max_redeem)
      return 'Min Redeem must be ≤ Max Redeem.';
    return null;
  };

  const fetchTenants = async () => {
    try {
      const res = await api.get<TenantOption[]>('/api/v1/admin/tenants/');
      setTenants(res.data || []);
    } catch {
      setSnackbar({ open: true, message: 'Failed to load tenants.', severity: 'error' });
    }
  };

  const fetchConfigs = async (tenantId: number) => {
    setLoadingConfigs(true);
    try {
      const res = await api.get<Omit<ConfigRow, 'saving' | 'error'>[]>('/api/v1/admin/redeem-configs', {
        params: { tenant_id: tenantId },
      });
      const data: ConfigRow[] = (res.data || []).map((item: Omit<ConfigRow, 'saving' | 'error'>) => ({
        ...item,
        saving: false,
        error: null,
      }));
      setRows(data);
    } catch {
      setSnackbar({ open: true, message: 'Failed to load redeem configs.', severity: 'error' });
    } finally {
      setLoadingConfigs(false);
    }
  };

  const handleSave = async (platformId: number) => {
    const row = rows.find(r => r.platform_id === platformId);
    if (!row) return;

    const validationError = validate(row);
    if (validationError) {
      updateRow(platformId, { error: validationError });
      return;
    }

    updateRow(platformId, { saving: true, error: null });

    try {
      const res = await api.put(
        `/api/v1/admin/redeem-configs/${platformId}`,
        {
          redeem_to_platform_enabled: row.redeem_to_platform_enabled,
          min_redeem: row.min_redeem,
          max_redeem: row.max_redeem,
        },
        { params: { tenant_id: selectedTenant } }
      );

      const updated = res.data as ConfigRow & { warning?: string };
      const { warning, ...configFields } = updated;

      updateRow(platformId, { ...configFields, saving: false, error: null });

      if (warning) {
        setSnackbar({ open: true, message: warning, severity: 'info' });
      } else {
        setSnackbar({ open: true, message: 'Saved successfully.', severity: 'success' });
      }
    } catch {
      updateRow(platformId, { saving: false, error: 'Failed to save. Please try again.' });
      setSnackbar({ open: true, message: 'Save failed.', severity: 'error' });
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchTenants();
    } else {
      const tid = parseInt(userTenantIdStr || '0');
      if (tid > 0) setSelectedTenant(tid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedTenant !== '') {
      fetchConfigs(selectedTenant as number);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Platform Redeem Settings
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        {isSuperAdmin ? (
          <FormControl sx={{ minWidth: 300 }}>
            <InputLabel>Select Tenant</InputLabel>
            <Select
              value={selectedTenant}
              label="Select Tenant"
              onChange={e => setSelectedTenant(Number(e.target.value))}
            >
              {tenants.map(t => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography variant="h6" color="primary">
            Managing redeem config for your tenant
          </Typography>
        )}
      </Paper>

      {selectedTenant === '' && isSuperAdmin && (
        <Typography color="text.secondary">
          Select a tenant to view redeem configurations.
        </Typography>
      )}

      {selectedTenant !== '' && (
        loadingConfigs ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 750 }}>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Platform</strong></TableCell>
                  <TableCell><strong>Auto-Redeem Enabled</strong></TableCell>
                  <TableCell><strong>Capabilities</strong></TableCell>
                  <TableCell><strong>Min Redeem</strong></TableCell>
                  <TableCell><strong>Max Redeem</strong></TableCell>
                  <TableCell><strong>Save</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => {
                  const toggleDisabled = !row.supports_balance_read || !row.supports_redeem;
                  const isAuto = row.redeem_to_platform_enabled && row.supports_balance_read && row.supports_redeem;

                  return (
                    <React.Fragment key={row.platform_id}>
                      <TableRow>
                        <TableCell>
                          <Typography variant="subtitle2">{row.platform_display_name}</Typography>
                        </TableCell>

                        <TableCell>
                          <Tooltip
                            title={toggleDisabled ? 'Redeem not supported for this platform yet — redeems go to manual staff.' : ''}
                            disableHoverListener={!toggleDisabled}
                            disableFocusListener={!toggleDisabled}
                            disableTouchListener={!toggleDisabled}
                          >
                            <span style={{ display: 'inline-flex' }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={row.redeem_to_platform_enabled}
                                    onChange={e => updateRow(row.platform_id, { redeem_to_platform_enabled: e.target.checked })}
                                    disabled={toggleDisabled || row.saving}
                                  />
                                }
                                label=""
                              />
                            </span>
                          </Tooltip>
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Chip
                              label="Balance Read"
                              size="small"
                              color={row.supports_balance_read ? 'success' : 'default'}
                            />
                            <Chip
                              label="Auto Redeem"
                              size="small"
                              color={row.supports_redeem ? 'success' : 'default'}
                            />
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={row.min_redeem ?? ''}
                            onChange={e => updateRow(row.platform_id, {
                              min_redeem: e.target.value === '' ? null : Number(e.target.value),
                            })}
                            disabled={row.saving}
                            inputProps={{ min: 0, step: 1 }}
                            sx={{ width: 120 }}
                            error={!!row.error}
                            placeholder="No limit"
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={row.max_redeem ?? ''}
                            onChange={e => updateRow(row.platform_id, {
                              max_redeem: e.target.value === '' ? null : Number(e.target.value),
                            })}
                            disabled={row.saving}
                            inputProps={{ min: 0, step: 1 }}
                            sx={{ width: 120 }}
                            error={!!row.error}
                            placeholder="No limit"
                          />
                        </TableCell>

                        <TableCell>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleSave(row.platform_id)}
                            disabled={row.saving}
                            startIcon={row.saving ? <CircularProgress size={16} /> : undefined}
                          >
                            {row.saving ? 'Saving...' : 'Save'}
                          </Button>
                        </TableCell>
                      </TableRow>

                      <TableRow sx={{ backgroundColor: 'grey.50' }}>
                        <TableCell colSpan={6} sx={{ py: 0.75, borderBottom: '1px solid rgba(224,224,224,0.6)' }}>
                          {row.error ? (
                            <Typography variant="caption" color="error">{row.error}</Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              {isAuto
                                ? 'Auto: validates balance, debits platform, then payout.'
                                : 'Manual: staff process via Discord/email.'}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlatformRedeemConfigPage;
